import { useCallback, useEffect, useMemo } from "react";

import {
    StatsigExperimentCohort,
    StatsigExperimentKey
} from "../../../../feature-gates/types";

import FeatureGates from "./feature-gate";
import { ConfirmationPageType } from "./use-confirmation-page-type";

import { useSearchParameters } from ".";

export type ExperimentCohort =
    | StatsigExperimentCohort.ATLAS_58859_JSW_FIND_USER_SEG_AND_TEMPLATE_REDESIGN_CONTROL
    | StatsigExperimentCohort.ATLAS_58859_JSW_FIND_USER_SEG_AND_TEMPLATE_REDESIGN_TREATMENT;

/**
 * useUserSegJiraTemplateRedesignExperiment
 *
 * Hook that determines to enroll a user into ATLAS_58859
 * @returns object
 */
export function useUserSegJiraTemplateRedesignExperiment(): {
    cohort: string;
    enrollmentPreconditionsMet: boolean;
    getConfirmationPageTypeFromEnrollment: (
        controlOption: ConfirmationPageType,
        treatmentOption: ConfirmationPageType
    ) => ConfirmationPageType;
} {
    const { products, n2n, templateId } = useSearchParameters();

    const enrollmentPreconditionsMet = useMemo(() => {
        const hasTemplateRecommendation = Boolean(templateId);
        const isProvisioningOnlyJSW =
            products.length === 1 && products[0] === "jira-software.ondemand";
        return !hasTemplateRecommendation && isProvisioningOnlyJSW && n2n;
    }, [templateId, products, n2n]);

    const cohort = useMemo(
        () =>
            FeatureGates.getExperimentValue<ExperimentCohort>(
                StatsigExperimentKey.ATLAS_58859_JSW_FIND_USER_SEG_AND_TEMPLATE_REDESIGN,
                "cohort",
                StatsigExperimentCohort.ATLAS_58859_JSW_FIND_USER_SEG_AND_TEMPLATE_REDESIGN_CONTROL,
                { fireExperimentExposure: false }
            ),
        []
    );

    const getConfirmationPageTypeFromEnrollment = useCallback(
        (control: ConfirmationPageType, treatment: ConfirmationPageType) => {
            if (!enrollmentPreconditionsMet) {
                return control;
            }

            return cohort ===
                StatsigExperimentCohort.ATLAS_58859_JSW_FIND_USER_SEG_AND_TEMPLATE_REDESIGN_TREATMENT
                ? treatment
                : control;
        },
        [cohort]
    );

    useEffect(() => {
        if (enrollmentPreconditionsMet) {
            FeatureGates.manuallyLogExperimentExposure(
                StatsigExperimentKey.ATLAS_58859_JSW_FIND_USER_SEG_AND_TEMPLATE_REDESIGN
            );
        }
    }, [enrollmentPreconditionsMet]);

    return {
        cohort,
        enrollmentPreconditionsMet,
        getConfirmationPageTypeFromEnrollment
    };
}
